import React, { useState, useEffect, useMemo, useRef } from 'react';
import { db, storage, auth, functions } from '../config/firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { doc, updateDoc, onSnapshot, DocumentData, getDoc, setDoc, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions'; 
import useAppStore from '../store/appStore';
import useAdminStore from '../store/adminStore'; 
import { 
    Calendar, PlusCircle, Lock, Upload, Loader2, ArrowRight, AlertTriangle, FileText, Edit3, Settings, Download, X, CheckCircle2, FileCheck, Trash2, ChevronDown
} from 'lucide-react';
import { calculateAllMetrics } from '../store/appStore'; 
import { ClientWelcomeHeader } from '../components/ClientWelcomeHeader';
import PasswordPrompt from '../components/PasswordPrompt';
import { resolveClientCoach } from '../utils/teamResolve';
import { isBillingPending, allocateNextCustomerNumber, simulateStripeActivation, resolveBestandMatchForActivation } from '../services/billingSimulationService';
import { PackageTier } from '../types/billing';
import { BESTAND_MANUAL_REVIEW_MSG, normalizeCustomerNumberCore } from '../utils/bestandMatch';
import { calculateClientMonthlyFee } from '../utils/clientPricing';
import { formatEuroDe, getCountryMwstSatz, netToGross } from '../utils/billingGross';

const VALID_PACKAGE_TIERS: PackageTier[] = ['Solo', 'TeamBase', 'TeamPro', 'TeamPro+'];

const resolveMandantPackageTier = (data: DocumentData | null | undefined): PackageTier => {
    if (!data) return 'TeamPro';
    const raw =
        data.packageTier ||
        data.tarifStatus ||
        data.clientData?.packageTier ||
        data.clientData?.tarifStatus ||
        '';
    const normalized = String(raw).trim();
    if (VALID_PACKAGE_TIERS.includes(normalized as PackageTier)) {
        return normalized as PackageTier;
    }
    if (data.isSolo === true || data.clientData?.isSolo === true) return 'Solo';
    if (data.hasEmployeeEval === true || data.clientData?.hasEmployeeEval === true) return 'TeamPro';
    if (data.hasEmployeeEval === false || data.clientData?.hasEmployeeEval === false) return 'TeamBase';
    return 'TeamPro';
};

/** Bei Freigabe: TeamPro+ nur wenn Umsatz-Schwelle schon erreicht — sonst Basis-Tarif. */
const tierForBillingActivation = (data: DocumentData | null | undefined): PackageTier => {
    const resolved = resolveMandantPackageTier(data);
    if (resolved !== 'TeamPro+') return resolved;
    const plusActive =
        data?.isTeamProPlus === true ||
        data?.clientData?.isTeamProPlus === true ||
        data?.insiderTeamleaderPlusChecked === true ||
        data?.clientData?.insiderTeamleaderPlusChecked === true;
    return plusActive ? 'TeamPro+' : 'TeamPro';
};

// ============================================================================
// STYLES: EXECUTIVE CARBON EDITION (NOBLE SLATE v11.0)
// ============================================================================
const DashboardStyles = () => (
    <style>{`
        @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;700;800&family=Inter:wght@300;400;600;800&display=swap');

        .font-sans { font-family: 'Inter', sans-serif; }
        .font-mono-numbers { font-family: 'JetBrains Mono', monospace; font-variant-numeric: tabular-nums; }
        
        .bg-deep-space { background-color: #030303; }
        html:not(.dark) .bg-deep-space { background-color: var(--pe-bg-primary); }
        html:not(.dark) .carbon-weave-bg { background-color: var(--pe-bg-elevated); background-image: none; }
        html:not(.dark) .carbon-panel { background-color: var(--pe-bg-card); border-color: var(--pe-border-subtle); }
        html:not(.dark) .carbon-panel::after { background: none; }
        .text-phantom-gold { color: #E6C299; }
        
        .carbon-weave-bg {
            background-color: #111;
            background-image: 
                radial-gradient(circle at 100% 150%, #111 24%, #1a1a1a 24%, #1a1a1a 28%, #111 28%, #111 36%, #1a1a1a 36%, #1a1a1a 40%, transparent 40%, transparent),
                radial-gradient(circle at 0 150%, #111 24%, #1a1a1a 24%, #1a1a1a 28%, #111 28%, #111 36%, #1a1a1a 36%, #1a1a1a 40%, transparent 40%, transparent),
                radial-gradient(circle at 50% 100%, #1a1a1a 10%, #111 10%, #111 23%, #1a1a1a 23%, #1a1a1a 30%, #111 30%, #111 43%, #1a1a1a 43%, #1a1a1a 50%, #111 50%, #111 63%, #1a1a1a 63%, #1a1a1a 71%, transparent 71%, transparent),
                radial-gradient(circle at 100% 50%, #1a1a1a 5%, #111 5%, #111 15%, #1a1a1a 15%, #1a1a1a 20%, #111 20%, #111 29%, #1a1a1a 29%, #1a1a1a 34%, #111 34%, #111 44%, #1a1a1a 44%, #1a1a1a 49%, transparent 49%, transparent),
                radial-gradient(circle at 0 50%, #1a1a1a 5%, #111 5%, #111 15%, #1a1a1a 15%, #1a1a1a 20%, #111 20%, #111 29%, #1a1a1a 29%, #1a1a1a 34%, #111 34%, #111 44%, #1a1a1a 44%, #1a1a1a 49%, transparent 49%, transparent);
            background-size: 20px 20px;
        }

        .carbon-console, .carbon-panel {
            position: relative;
            border-radius: 1.5rem;
            border: 1px solid #2a2a2a;
            box-shadow: 0 30px 60px -15px rgba(0,0,0,1), inset 0 2px 5px rgba(255,255,255,0.05);
        }
        
        .carbon-console { border-top: 1px solid #444; padding: 2.5rem 2rem; margin-bottom: 2.5rem; }

        .carbon-console::after, .carbon-panel::after {
            content: ''; position: absolute; inset: 0; background: linear-gradient(to bottom, transparent, rgba(0,0,0,0.6)); border-radius: inherit; pointer-events: none;
        }

        .carbon-content { position: relative; z-index: 10; }

        /* NEON LED COLORS */
        .led-purple  { color: #c084fc; text-shadow: 0 0 10px rgba(192,132,252,0.6), 0 0 20px rgba(192,132,252,0.3); }
        .led-emerald { color: #10b981; text-shadow: 0 0 10px rgba(16,185,129,0.6), 0 0 20px rgba(16,185,129,0.3); }
        .led-lime    { color: #a3e635; text-shadow: 0 0 10px rgba(163,230,53,0.6),  0 0 20px rgba(163,230,53,0.3); }
        .led-amber   { color: #fbbf24; text-shadow: 0 0 10px rgba(251,191,36,0.6),  0 0 20px rgba(251,191,36,0.3); }
        .led-orange  { color: #fb923c; text-shadow: 0 0 10px rgba(251,146,60,0.6),  0 0 20px rgba(251,146,60,0.3); }
        .led-red     { color: #ef4444; text-shadow: 0 0 10px rgba(239,68,68,0.6),   0 0 20px rgba(239,68,68,0.3); }
        .led-gold    { color: #E6C299; text-shadow: 0 0 10px rgba(230,194,153,0.6), 0 0 20px rgba(230,194,153,0.3); }
        .led-white   { color: #fff; text-shadow: 0 0 15px rgba(255,255,255,0.5); }

        .metal-badge {
            background: linear-gradient(to bottom, #222, #0a0a0a);
            border: 1px solid #333;
            box-shadow: inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 10px rgba(0,0,0,0.8);
            border-radius: 6px;
        }

        .toggle-btn { background: linear-gradient(to bottom, #1a1a1a, #0a0a0a); border: 1px solid #333; color: #888; transition: all 0.2s; box-shadow: inset 0 1px 0 rgba(255,255,255,0.05); }
        .toggle-btn.active { background: linear-gradient(to bottom, #E6C299, #b48a56); color: #000; border-color: #E6C299; box-shadow: 0 0 15px rgba(230,194,153,0.4); font-weight: 900;}

        /* Light Mode: Carbon → Warm-Platin (Dark-Regeln oben unverändert) */
        html:not(.dark) .bg-deep-space { background-color: var(--pe-bg-primary); }
        html:not(.dark) .text-phantom-gold { color: var(--pe-accent-text); }
        html:not(.dark) .carbon-weave-bg {
            background-image: none;
            background-color: var(--pe-bg-elevated);
        }
        html:not(.dark) .carbon-console,
        html:not(.dark) .carbon-panel {
            border: 1px solid var(--pe-border-subtle);
            box-shadow: 0 12px 28px -12px rgba(28, 25, 23, 0.18);
            background-color: var(--pe-bg-card);
        }
        html:not(.dark) .carbon-console { border-top: 1px solid var(--pe-border-strong); }
        html:not(.dark) .carbon-console::after,
        html:not(.dark) .carbon-panel::after {
            background: none;
        }
        html:not(.dark) .metal-badge {
            background: linear-gradient(to bottom, var(--pe-bg-elevated), var(--pe-bg-tile-header));
            border: 1px solid var(--pe-border-subtle);
            box-shadow: inset 0 1px 0 rgba(255,255,255,0.8), 0 2px 6px rgba(28,25,23,0.06);
        }
        html:not(.dark) .toggle-btn {
            background: linear-gradient(to bottom, var(--pe-bg-card), var(--pe-bg-tile-header));
            border: 1px solid var(--pe-border-strong);
            color: var(--pe-text-muted);
            box-shadow: inset 0 1px 0 rgba(255,255,255,0.6);
        }
        html:not(.dark) .led-purple  { color: #7C3AED; text-shadow: none; }
        html:not(.dark) .led-emerald { color: #047857; text-shadow: none; }
        html:not(.dark) .led-lime    { color: #4D7C0F; text-shadow: none; }
        html:not(.dark) .led-amber   { color: #B45309; text-shadow: none; }
        html:not(.dark) .led-orange  { color: #C2410C; text-shadow: none; }
        html:not(.dark) .led-red     { color: #B91C1C; text-shadow: none; }
        html:not(.dark) .led-gold    { color: var(--pe-accent-text); text-shadow: none; }
        html:not(.dark) .led-white   { color: var(--pe-text-primary); text-shadow: none; }

        .pe-help-tip {
            position: relative;
        }
        .pe-help-tip-bubble {
            display: none;
            position: absolute;
            z-index: 80;
            left: 1.25rem;
            right: auto;
            bottom: calc(100% + 10px);
            top: auto;
            width: min(28rem, calc(100% - 2rem));
            max-width: 28rem;
            padding: 0.85rem 1rem;
            border-radius: 0.75rem;
            background: #0f1015;
            border: 1px solid rgba(230,194,153,0.45);
            color: #e2e8f0;
            font-size: 0.75rem;
            line-height: 1.45;
            font-weight: 500;
            box-shadow: 0 12px 30px rgba(0,0,0,0.55);
            white-space: normal;
            pointer-events: none;
        }
        .pe-help-tip-bubble::after {
            content: '';
            position: absolute;
            left: 1.5rem;
            top: 100%;
            border: 7px solid transparent;
            border-top-color: rgba(230,194,153,0.45);
        }
        html:not(.dark) .pe-help-tip-bubble {
            background: #fff;
            color: #1c1917;
            border-color: rgba(141,100,53,0.35);
            box-shadow: 0 10px 24px rgba(28,25,23,0.12);
        }
        html:not(.dark) .pe-help-tip-bubble::after {
            border-top-color: rgba(141,100,53,0.35);
        }
        @media (hover: hover) {
            .pe-help-tip:hover > .pe-help-tip-bubble { display: block; }
        }
    `}</style>
);

interface ClientDashboardProps {
    navigateTo?: (page: string) => void;
    showNotification?: (msg: string, type: 'success' | 'error') => void;
}

const ClientDashboard: React.FC<ClientDashboardProps> = ({ navigateTo, showNotification }) => {
    
    const appStore = useAppStore() as any;
    const clientData = appStore.clientData;
    const settings = appStore.settings; 
    const setClientData = appStore.setClientData; 

    const executeJahreswechsel = appStore.executeJahreswechsel;
    const rolloverConfirm = appStore.rolloverConfirm;
    const setRolloverConfirm = appStore.setRolloverConfirm;
    const addNewYear = appStore.addNewYear;
    const updateYearBwaUrl = appStore.updateYearBwaUrl;
    
    const { impersonatedClientId, adminTeam } = useAdminStore(); 
    
    const [userData, setUserData] = useState<DocumentData | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [isDeletingUrl, setIsDeletingUrl] = useState<string | null>(null);
    const [yearNeedingUpload, setYearNeedingUpload] = useState<number | null>(null);
    const [showBwaModal, setShowBwaModal] = useState(false);

    const [devTargetYear, setDevTargetYear] = useState<number | null>(null);

    const [showNervPopup, setShowNervPopup] = useState(false);
    const [pendingYearToOpen, setPendingYearToOpen] = useState<any>(null);
    const [isSubmittingToCoach, setIsSubmittingToCoach] = useState(false);

    const [isBillingLoading, setIsBillingLoading] = useState(false);
    const [isCoachApproving, setIsCoachApproving] = useState(false);
    const coachApproveInFlight = useRef(false);
    const [approvePasswordOpen, setApprovePasswordOpen] = useState(false);
    const [pendingApproveYear, setPendingApproveYear] = useState<any>(null);

    const [isRescuing, setIsRescuing] = useState(false); // NEU: Loading State für den Geister-Akte Fix
    const [expandedYears, setExpandedYears] = useState<Record<string, boolean>>({});

    const team = appStore.team || [];
    const teamRegistry = appStore.teamRegistry || [];

    const currentUserId = auth.currentUser?.uid;
    const currentUserProfile = adminTeam?.find(m => m.id === currentUserId);
    const currentUserEmail = auth.currentUser?.email?.toLowerCase() || '';
    const isAdminUser = 
        currentUserEmail === 'erfolg@friseur-coaching.de' || 
        currentUserEmail.includes('christian') || 
        currentUserProfile?.role === 'admin' || 
        currentUserProfile?.role === 'superadmin';

    const jahre = useMemo(() => {
        return (clientData?.geschaeftsjahre && clientData.geschaeftsjahre.length > 0)
            ? clientData.geschaeftsjahre
            : (userData?.geschaeftsjahre || [{ jahr: 2027, status: 'datenerfassung' }]);
    }, [clientData?.geschaeftsjahre, userData?.geschaeftsjahre]);

    /** Gottmodus: Netto/Brutto für Abbuchung (MwSt aus globalen Country-Daten). */
    const coachBillingPreview = useMemo(() => {
        if (!impersonatedClientId) return null;
        const mergedClient = {
            ...(userData || {}),
            ...(clientData || {}),
            clientData: { ...(userData?.clientData || {}), ...(clientData || {}) },
        };
        const isProBono =
            mergedClient.proBonoActive === true ||
            mergedClient.isProBono === true ||
            mergedClient.clientData?.proBonoActive === true;
        if (isProBono) {
            return { isProBono: true as const, netto: 0, brutto: 0, mwstSatz: 0 };
        }
        const fixedRaw = mergedClient.monthlyFee ?? mergedClient.clientData?.monthlyFee;
        let netto = 0;
        if (typeof fixedRaw === 'number' && Number.isFinite(fixedRaw) && fixedRaw > 0) {
            netto = fixedRaw;
        } else if (typeof fixedRaw === 'string' && fixedRaw.trim()) {
            const parsed = parseFloat(fixedRaw.trim().replace(/\./g, '').replace(',', '.'));
            if (Number.isFinite(parsed) && parsed > 0) netto = parsed;
        }
        if (netto <= 0) {
            netto = calculateClientMonthlyFee({
                settings,
                client: mergedClient,
                isProBono: false,
                mandantNr: mergedClient.mandantNr || mergedClient.clientData?.mandantNr,
            });
        }
        const mwstSatz = getCountryMwstSatz(settings);
        const brutto = netToGross(netto, mwstSatz);
        return { isProBono: false as const, netto, brutto, mwstSatz };
    }, [impersonatedClientId, clientData, userData, settings]);

    const latestJahrNum = useMemo(() => {
        if (!jahre.length) return null;
        return Math.max(...jahre.map((j: any) => Number(j.jahr) || 0));
    }, [jahre]);

    const hasBranches = !!(clientData?.hasBranches || userData?.hasBranches || userData?.clientData?.hasBranches);
    const branchList = useMemo(() => {
        const raw = clientData?.branches || userData?.branches || userData?.clientData?.branches || [];
        return Array.isArray(raw) ? raw : [];
    }, [clientData?.branches, userData?.branches, userData?.clientData?.branches]);

    const isYearExpanded = (jahr: number | string) => {
        const key = String(jahr);
        if (Object.prototype.hasOwnProperty.call(expandedYears, key)) return !!expandedYears[key];
        // Mit Filialen: nur das neueste Jahr standardmäßig aufgeklappt — alte Jahre nur Jahreszahl
        if (!(hasBranches && branchList.length > 0)) return false;
        return Number(jahr) === Number(latestJahrNum);
    };

    const toggleYearExpanded = (jahr: number | string) => {
        const key = String(jahr);
        setExpandedYears((prev) => ({
            ...prev,
            [key]: !isYearExpanded(jahr),
        }));
    };

    const needsCoachAttention = useMemo(() => {
        if (!impersonatedClientId) return false;
        return jahre.some((j: any) => j.status === 'angefragt' || (j.status === 'datenerfassung' && j.datenEingereicht));
    }, [jahre, impersonatedClientId]);

    useEffect(() => {
        if (jahre && jahre.length > 0 && !devTargetYear) {
            setDevTargetYear(Math.max(...jahre.map((j: any) => Number(j.jahr))));
        }
    }, [jahre, devTargetYear]);

    useEffect(() => {
        const user = auth.currentUser;
        
        const safeImpersonatedId = (impersonatedClientId === "undefined" || impersonatedClientId === "null") ? null : impersonatedClientId;
        const targetUid = safeImpersonatedId || user?.uid;

        if (!targetUid) return;

        const unsub = onSnapshot(doc(db, 'kalkulation_mandanten', targetUid), (docSnap) => {
            if (docSnap.exists()) {
                setUserData(docSnap.data());
            }
        }, (error) => {
            console.error("FATAL: Client Data Listener abgestürzt:", error);
        });
        
        return () => unsub();
    }, [impersonatedClientId]);

    const handleCreateNewYear = async () => {
        try {
            const user = auth.currentUser;
            const targetUid = impersonatedClientId || user?.uid;
            
            if (!targetUid) throw new Error("Keine Ziel-ID gefunden");

            const docRef = doc(db, 'kalkulation_mandanten', targetUid);
            const freshSnap = await getDoc(docRef);
            const freshData = freshSnap.exists() ? freshSnap.data() : {};
            const currentYears = freshData?.clientData?.geschaeftsjahre || freshData?.geschaeftsjahre || [];

            const nextYear = currentYears.length > 0 
                ? Math.max(...currentYears.map((j: any) => Number(j.jahr))) + 1 
                : 2027;

            const alreadyExists = currentYears.some((j: any) => Number(j.jahr) === nextYear);
            if (alreadyExists) {
                if (showNotification) showNotification(`Jahr ${nextYear} existiert bereits.`, 'error');
                return;
            }

            const confirmed = window.confirm(
                `Neues Geschäftsjahr ${nextYear} wirklich anlegen?\n\n` +
                `Das legt eine neue Jahres-Akte an (Status: Datenerfassung).\n` +
                `Voraussetzung: Der System-Rollover für das Vorjahr ist durch.\n\n` +
                `Das passiert NICHT automatisch beim Speichern von Stammdaten.`
            );
            if (!confirmed) return;

            const updatedJahre = [...currentYears, {
                jahr: nextYear,
                status: 'datenerfassung', 
                bwaUrl: '',
                bwaUrls: [] 
            }];

            const rawSnapshot = calculateAllMetrics(clientData, settings);
            const cleanSnapshot = JSON.parse(JSON.stringify(rawSnapshot));
            
            delete cleanSnapshot.clientData;
            delete cleanSnapshot.settings;
            delete cleanSnapshot.bwaData;

            await updateDoc(docRef, {
                "clientData.geschaeftsjahre": updatedJahre,
                geschaeftsjahre: updatedJahre,
                "clientData.vorjahresCalcData": cleanSnapshot
            });

            if (typeof setClientData === 'function') {
                const freshClientData = freshData.clientData || {};
                setClientData({ ...freshClientData, geschaeftsjahre: updatedJahre, vorjahresCalcData: cleanSnapshot });
            }

            addNewYear(nextYear, '', cleanSnapshot);

            if (showNotification) showNotification(`Jahr ${nextYear} erfolgreich angelegt!`, 'success');
        } catch (err) {
            console.error("Fehler beim Anlegen:", err);
            if (showNotification) showNotification("Fehler beim Anlegen.", 'error');
        }
    };

    // 💉 NEU: Der Notfall-Fix für kopierte/importierte Geister-Akten
    const handleRescueGhostAkte = async () => {
        const user = auth.currentUser;
        const targetUid = impersonatedClientId || user?.uid;
        if (!targetUid) return;

        if (!window.confirm("Dieses Tool initialisiert die fehlende Geschäftsjahre-Struktur (2027) in Firebase. Deine bisherigen Daten bleiben vollständig erhalten. Ausführen?")) return;

        setIsRescuing(true);
        try {
            const defaultJahre = [{
                jahr: 2027,
                status: 'datenerfassung',
                bwaUrl: '',
                bwaUrls: []
            }];

            const docRef = doc(db, 'kalkulation_mandanten', targetUid);

            // Schreibe das Array an BEIDE Orte, damit das Admin-Dashboard und die App es finden
            await updateDoc(docRef, {
                "clientData.geschaeftsjahre": defaultJahre,
                "geschaeftsjahre": defaultJahre
            });

            const yearDocRef = doc(db, 'kalkulation_mandanten', targetUid, 'jahre', '2027');
            await setDoc(yearDocRef, {
                bwaUrl: '',
                bwaUrls: [],
                clientData: {
                    bwaUrl: '',
                    bwaUrls: []
                }
            }, { merge: true });

            if (typeof setClientData === 'function' && clientData) {
                setClientData({ ...clientData, geschaeftsjahre: defaultJahre });
            }

            if (showNotification) {
                showNotification('Geister-Akte erfolgreich repariert! Struktur ist jetzt vorhanden.', 'success');
            } else {
                alert('Geister-Akte erfolgreich repariert!');
            }
        } catch (error: any) {
            console.error("Fehler beim Reparieren der Geister-Akte:", error);
            if (showNotification) showNotification(`Fehler: ${error.message}`, 'error');
        } finally {
            setIsRescuing(false);
        }
    };

    const handleLateBwaUpload = async (e: React.ChangeEvent<HTMLInputElement>, targetYear: number | null) => {
        if (!targetYear) return;

        try {
            const files = Array.from(e.target.files || []);
            if (files.length === 0) return;

            const allValid = files.every(f => f.type === 'application/pdf');
            if (!allValid) {
                alert("Bitte lade ausschließlich originale PDF-Dokumente (keine Fotos) hoch.");
                if (e.target) e.target.value = '';
                return;
            }

            setIsUploading(true);
            
            const user = auth.currentUser;
            const targetUid = impersonatedClientId || user?.uid;
            
            if (!targetUid) throw new Error("Keine Ziel-ID gefunden");

            const safeUid = String(targetUid);
            const safeYear = String(targetYear);

            const docRef = doc(db, 'kalkulation_mandanten', safeUid);
            const freshSnap = await getDoc(docRef);
            const freshData = freshSnap.exists() ? freshSnap.data() : {};
            const currentYears = freshData?.clientData?.geschaeftsjahre || freshData?.geschaeftsjahre || [];
            
            const targetYearData = currentYears.find((j: any) => Number(j.jahr) === Number(targetYear));
            const existingBwaUrls = targetYearData?.bwaUrls || (targetYearData?.bwaUrl ? [targetYearData.bwaUrl] : []);
            
            const newUrls: string[] = [];

            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const timestamp = Date.now();
                const fileRef = ref(storage, `kalkulation_mandanten/${safeUid}/uploads/BWA_${safeYear}_${timestamp}_${i}.pdf`);
                
                const metadata = { contentType: 'application/pdf' };
                const snapshot = await uploadBytes(fileRef, file, metadata);
                const downloadUrl = await getDownloadURL(snapshot.ref);
                newUrls.push(downloadUrl);
            }

            const finalUrls = [...existingBwaUrls, ...newUrls];

            const updatedJahre = currentYears.map((j: any) => 
                Number(j.jahr) === Number(targetYear) ? { ...j, bwaUrl: finalUrls[0], bwaUrls: finalUrls } : j
            );

            await updateDoc(docRef, {
                "clientData.geschaeftsjahre": updatedJahre,
                geschaeftsjahre: updatedJahre
            });

            const yearDocRef = doc(db, 'kalkulation_mandanten', safeUid, 'jahre', safeYear);
            await setDoc(yearDocRef, {
                bwaUrl: finalUrls[0],
                bwaUrls: finalUrls,
                clientData: {
                    bwaUrl: finalUrls[0],
                    bwaUrls: finalUrls
                }
            }, { merge: true });

            if (typeof setClientData === 'function' && clientData) {
                setClientData({ ...clientData, geschaeftsjahre: updatedJahre });
            }

            if (typeof updateYearBwaUrl === 'function') {
                updateYearBwaUrl(targetYear, finalUrls[0]);
            }

            if (showNotification) {
                showNotification(`${files.length} BWA-Dokument(e) für ${targetYear} erfolgreich hochgeladen!`, 'success');
            }
        } catch (err) {
            console.error("Upload Fehler:", err);
            if (showNotification) showNotification("Fehler beim Upload.", 'error');
        } finally {
            setIsUploading(false);
            if (e.target) e.target.value = '';
        }
    };

    const handleDeleteSingleBwa = async (urlToDelete: string, targetYear: number | null) => {
        if (!targetYear || !urlToDelete) return;
        if (!window.confirm("Dieses PDF wirklich unwiderruflich löschen?")) return;

        setIsDeletingUrl(urlToDelete);
        try {
            const user = auth.currentUser;
            const targetUid = impersonatedClientId || user?.uid;
            if (!targetUid) throw new Error("Keine Ziel-ID gefunden");

            const safeUid = String(targetUid);
            const safeYear = String(targetYear);

            try {
                const fileRef = ref(storage, urlToDelete);
                await deleteObject(fileRef);
            } catch (storageErr) {
                console.warn("Storage-Löschung fehlgeschlagen (evtl. schon gelöscht):", storageErr);
            }

            const docRef = doc(db, 'kalkulation_mandanten', safeUid);
            const freshSnap = await getDoc(docRef);
            const freshData = freshSnap.exists() ? freshSnap.data() : {};
            const currentYears = freshData?.clientData?.geschaeftsjahre || freshData?.geschaeftsjahre || [];
            
            const targetYearData = currentYears.find((j: any) => Number(j.jahr) === Number(targetYear));
            const existingBwaUrls = targetYearData?.bwaUrls || (targetYearData?.bwaUrl ? [targetYearData.bwaUrl] : []);
            
            const finalUrls = existingBwaUrls.filter((u: string) => u !== urlToDelete);
            const newMainUrl = finalUrls.length > 0 ? finalUrls[0] : '';

            const updatedJahre = currentYears.map((j: any) => 
                Number(j.jahr) === Number(targetYear) ? { ...j, bwaUrl: newMainUrl, bwaUrls: finalUrls } : j
            );

            await updateDoc(docRef, {
                "clientData.geschaeftsjahre": updatedJahre,
                geschaeftsjahre: updatedJahre
            });

            const yearDocRef = doc(db, 'kalkulation_mandanten', safeUid, 'jahre', safeYear);
            await setDoc(yearDocRef, {
                bwaUrl: newMainUrl,
                bwaUrls: finalUrls,
                clientData: {
                    bwaUrl: newMainUrl,
                    bwaUrls: finalUrls
                }
            }, { merge: true });

            if (typeof setClientData === 'function' && clientData) {
                setClientData({ ...clientData, geschaeftsjahre: updatedJahre });
            }

            if (showNotification) {
                showNotification("PDF erfolgreich gelöscht.", 'success');
            }
        } catch (err) {
            console.error("Fehler beim Löschen:", err);
            if (showNotification) showNotification("Fehler beim Löschen der Datei.", 'error');
        } finally {
            setIsDeletingUrl(null);
        }
    };

    const proceedToYear = async (jahr: number, status: string, branchId?: string) => {
        if (navigateTo) {
            localStorage.setItem('CURRENT_YEAR_STATUS', status);
            localStorage.setItem('CURRENT_YEAR', jahr.toString());
            try {
                sessionStorage.setItem('CURRENT_YEAR', jahr.toString());
            } catch (_) { /* ignore */ }
            if (branchId) {
                try {
                    localStorage.setItem('CURRENT_BRANCH', branchId);
                    sessionStorage.setItem('CURRENT_BRANCH', branchId);
                } catch (_) { /* ignore */ }
                if (typeof appStore.setActiveBranch === 'function') {
                    await appStore.setActiveBranch(branchId);
                }
            } else {
                const uid = impersonatedClientId || auth.currentUser?.uid;
                if (uid && typeof appStore.initializeStore === 'function') {
                    await appStore.initializeStore(uid);
                }
            }
            navigateTo('startseite'); 
        }
    };

    const openYearWithNervCheck = (jahrItem: any) => {
        if (!impersonatedClientId && jahrItem.status === 'datenerfassung') {
            setPendingYearToOpen(jahrItem);
            setShowNervPopup(true);
        } else {
            proceedToYear(jahrItem.jahr, jahrItem.status);
        }
    };

    const handleOpenYear = (jahrItem: any) => {
        if (!impersonatedClientId && jahrItem.status === 'angefragt') {
            if (showNotification) showNotification('Dieses Jahr befindet sich noch in der Prüfung durch deinen Coach.', 'error');
            return;
        }

        const hasBwa = jahrItem.bwaUrl || (jahrItem.bwaUrls && jahrItem.bwaUrls.length > 0);

        if (!hasBwa && jahrItem.status === 'datenerfassung') {
            setYearNeedingUpload(jahrItem.jahr);
            if (showNotification) showNotification(`Achtung: Die BWA für ${jahrItem.jahr} fehlt noch!`, 'error');
            return;
        }
        
        openYearWithNervCheck(jahrItem);
    };

    const handleNotifyCoach = async (e: React.MouseEvent, jahrItem: any) => {
        e.stopPropagation(); 
        
        const hasBwa = jahrItem.bwaUrl || (jahrItem.bwaUrls && jahrItem.bwaUrls.length > 0);

        if (!hasBwa) {
            if (showNotification) {
                showNotification("Achtung: Du musst zuerst die BWA hochladen, bevor du die Daten freigeben kannst!", 'error');
            } else {
                alert("Achtung: Du musst zuerst die finale BWA hochladen, bevor du die Daten freigeben kannst!");
            }
            setYearNeedingUpload(jahrItem.jahr);
            return;
        }

        if (!window.confirm(`Hast du wirklich ALLE erforderlichen Daten für das Jahr ${jahrItem.jahr} eingetragen? Die Akte wird zur Prüfung an deinen Coach gesendet und vorerst gesperrt.`)) return;

        setIsSubmittingToCoach(true);
        try {
            const user = auth.currentUser;
            const targetUid = impersonatedClientId || user?.uid;
            
            if (!targetUid) throw new Error("Keine Ziel-ID gefunden");

            const docRef = doc(db, 'kalkulation_mandanten', targetUid);
            const freshSnap = await getDoc(docRef);
            const freshData = freshSnap.exists() ? freshSnap.data() : {};
            const currentYears = freshData?.clientData?.geschaeftsjahre || freshData?.geschaeftsjahre || [];
            
            const updatedJahre = currentYears.map((j: any) => 
                Number(j.jahr) === jahrItem.jahr 
                    ? { ...j, status: 'angefragt', datenEingereicht: true, datenEingereichtAm: new Date().toISOString() } 
                    : j
            );

            await updateDoc(docRef, {
                "clientData.geschaeftsjahre": updatedJahre,
                geschaeftsjahre: updatedJahre
            });

            if (typeof setClientData === 'function' && clientData) {
                setClientData({ ...clientData, geschaeftsjahre: updatedJahre });
            }

            // Meldeträger: mail_queue → Betreuer (Backend/Prompt-KI verarbeitet type: kalkulation_ready)
            const coach = resolveClientCoach(
                { ...freshData, clientData: { ...(freshData.clientData || {}), ...(clientData || {}) } },
                team,
                teamRegistry
            );
            const coachEmail = String(coach?.email || '').trim() || 'erfolg@friseur-coaching.de';
            const salonName =
                clientData?.firmenname ||
                clientData?.salonname ||
                freshData?.firmenname ||
                freshData?.clientData?.firmenname ||
                'Mandant';
            const mandantNr =
                clientData?.mandantNr ||
                freshData?.mandantNr ||
                freshData?.clientData?.mandantNr ||
                '';

            try {
                await addDoc(collection(db, 'mail_queue'), {
                    email: coachEmail,
                    type: 'kalkulation_ready',
                    subject: `Kalkulation prüfbereit: ${salonName} (${jahrItem.jahr})`,
                    participantName: salonName,
                    salonName,
                    mandantNr,
                    jahr: jahrItem.jahr,
                    applicantUid: targetUid,
                    coachName: coach?.name || '',
                    message:
                        `Der Mandant ${salonName} hat seine Eintragungen für das Jahr ${jahrItem.jahr} abgeschlossen.`,
                    appBaseUrl: window.location.origin,
                    status: 'pending',
                    createdAt: serverTimestamp(),
                });
            } catch (mailErr) {
                console.error('mail_queue kalkulation_ready fehlgeschlagen:', mailErr);
            }

            if (showNotification) {
                showNotification(`Erfolg! Coach wurde benachrichtigt und Jahr ${jahrItem.jahr} wird geprüft.`, 'success');
            } else {
                alert(`Erfolg! Coach wurde benachrichtigt und Jahr ${jahrItem.jahr} wird geprüft.`);
            }
        } catch (error) {
            console.error("Fehler beim Benachrichtigen des Coaches:", error);
            if (showNotification) showNotification("Fehler beim Senden.", 'error');
        } finally {
            setIsSubmittingToCoach(false);
        }
    };

    const requestCoachApprovalAndBilling = (e: React.MouseEvent, jahrItem: any) => {
        e.stopPropagation();
        if (!impersonatedClientId) return;
        setPendingApproveYear(jahrItem);
        setApprovePasswordOpen(true);
    };

    const handleCoachApprovalAndBilling = async (jahrItem: any) => {
        if (!impersonatedClientId || !jahrItem) return;
        if (coachApproveInFlight.current || isCoachApproving) return;

        if (!window.confirm(`Möchtest du das Jahr ${jahrItem.jahr} jetzt final freischalten (Status: Aktiv) UND die Zahlung via Stripe einleiten?`)) return;

        coachApproveInFlight.current = true;
        setIsCoachApproving(true);
        try {
            const docRef = doc(db, 'kalkulation_mandanten', impersonatedClientId);
            const freshSnap = await getDoc(docRef);
            const freshData = freshSnap.exists() ? freshSnap.data() : {};
            const currentYears = freshData?.clientData?.geschaeftsjahre || freshData?.geschaeftsjahre || [];

            const existingYear = currentYears.find((j: any) => Number(j.jahr) === Number(jahrItem.jahr));
            if (String(existingYear?.status || '').toLowerCase() === 'aktiv') {
                if (showNotification) {
                    showNotification(`Jahr ${jahrItem.jahr} ist bereits aktiv — Freigabe-Mail wird nicht erneut gesendet.`, 'success');
                }
                return;
            }

            const updatedJahre = currentYears.map((j: any) =>
                Number(j.jahr) === Number(jahrItem.jahr) ? { ...j, status: 'aktiv' } : j
            );

            // 1) Nur Status→aktiv — onMandantUpdated sendet Freigabe-Mails genau auf dieser Kante
            await updateDoc(docRef, {
                "clientData.geschaeftsjahre": updatedJahre,
                geschaeftsjahre: updatedJahre
            });

            // 2) Folgeschritte silent — CF blockiert Mail-Trigger bei lastSilentAdminUpdate
            const silentTs = Date.now();
            await updateDoc(docRef, { lastSilentAdminUpdate: silentTs });

            if (typeof setClientData === 'function' && clientData) {
                setClientData({ ...clientData, geschaeftsjahre: updatedJahre });
            }

            try {
                localStorage.setItem('CURRENT_YEAR', String(jahrItem.jahr));
                localStorage.setItem('CURRENT_YEAR_STATUS', 'aktiv');
                window.dispatchEvent(new CustomEvent('pe-year-status', {
                    detail: { year: String(jahrItem.jahr), status: 'aktiv' },
                }));
            } catch (_) { /* ignore */ }

            // Erste Freigabe / Bestand-Vorab: Abrechnung + ggf. SEPA-Sentinel
            const isBestandOrVorab = !!(
                freshData?.isExistingCustomer ||
                freshData?.isBestandskunde ||
                freshData?.isLegacy ||
                freshData?.clientData?.isExistingCustomer ||
                freshData?.clientData?.isBestandskunde ||
                freshData?.clientData?.isLegacy
            );
            const pendingBilling =
                freshData?.billingStatus === 'pending_approval' ||
                freshData?.clientData?.billingStatus === 'pending_approval' ||
                String(freshData?.customerNumber || '').trim() === 'PENDING' ||
                (isBestandOrVorab && freshData?.billingStatus !== 'active');

            if (isBestandOrVorab) {
                const emailForMatch = String(
                    freshData?.email ||
                        freshData?.mailadresse ||
                        freshData?.clientData?.mailadresse ||
                        freshData?.clientData?.email ||
                        ''
                ).trim();
                const kdForMatch = String(
                    freshData?.customerNumber ||
                        freshData?.existingCustomerNumber ||
                        freshData?.providedOldId ||
                        freshData?.clientData?.customerNumber ||
                        freshData?.clientData?.existingCustomerNumber ||
                        freshData?.clientData?.providedOldId ||
                        // Fallback: Vertragsbeginn / Kern aus Mandant-Nr. (z. B. 01/2018)
                        freshData?.vertragsbeginn ||
                        freshData?.clientData?.vertragsbeginn ||
                        freshData?.mandantNr ||
                        freshData?.clientData?.mandantNr ||
                        ''
                ).trim();
                try {
                    const match = await resolveBestandMatchForActivation(emailForMatch, kdForMatch);
                    // Etablierte Hauptakte (kein Legacy-Vorab): E-Mail-Treffer auf sich selbst reicht
                    const selfEstablished =
                        !freshData?.isLegacy &&
                        (freshData?.isBestandskunde === true || freshData?.isExistingCustomer === true) &&
                        (!!freshData?.stripeSetupComplete ||
                            freshData?.billingStatus === 'active' ||
                            freshData?.clientData?.billingStatus === 'active');
                    if (match.needsManualReview && !(selfEstablished && match.vorabId === impersonatedClientId)) {
                        await updateDoc(docRef, {
                            needsManualBestandReview: true,
                            bestandMatched: false,
                            lastSilentAdminUpdate: Date.now(),
                        });
                        if (showNotification) {
                            showNotification(match.message || BESTAND_MANUAL_REVIEW_MSG, 'error');
                        }
                    } else if (match.matched || selfEstablished) {
                        const patch: Record<string, any> = {
                            needsManualBestandReview: false,
                            bestandMatched: true,
                            matchedVorabId: match.vorabId || impersonatedClientId,
                            lastSilentAdminUpdate: Date.now(),
                        };
                        if (typeof match.monthlyFee === 'number') {
                            patch.monthlyFee = match.monthlyFee;
                            patch['clientData.monthlyFee'] = match.monthlyFee;
                        }
                        // Fehlende Kd.-Nr. aus Mandant-Kern nachziehen (Anzeige / künftige Matches)
                        if (!String(freshData?.customerNumber || '').trim() && kdForMatch) {
                            const core = normalizeCustomerNumberCore(kdForMatch);
                            if (core) {
                                patch.customerNumber = core;
                                patch.existingCustomerNumber = core;
                                patch['clientData.customerNumber'] = core;
                                patch['clientData.existingCustomerNumber'] = core;
                            }
                        }
                        await updateDoc(docRef, patch);
                    }
                } catch (matchErr) {
                    console.warn('Bestand-Match bei Freischaltung:', matchErr);
                }
            }

            if (pendingBilling) {
                const kd = String(freshData?.customerNumber || '').trim();
                // Nur Neukunden ohne echte Kd.-Nr. bekommen eine neue Nummer
                if (kd === 'PENDING' || (!kd && !isBestandOrVorab)) {
                    await allocateNextCustomerNumber(impersonatedClientId);
                }
                await simulateStripeActivation(
                    impersonatedClientId,
                    tierForBillingActivation(freshData)
                );
            }

            // Stripe über europe-west1 (nicht Default us-central1)
            try {
                const startBilling = httpsCallable(functions, 'startProjectAndBilling');
                await startBilling({ orderId: impersonatedClientId });
                if (showNotification) {
                    showNotification(`Jahr ${jahrItem.jahr} freigeschaltet & Abrechnung eingeleitet!`, 'success');
                }
            } catch (billErr: any) {
                console.error('Stripe startProjectAndBilling fehlgeschlagen:', billErr);
                const detail = billErr?.message || billErr?.code || 'Unbekannter Stripe-Fehler';
                if (showNotification) {
                    showNotification(
                        `Jahr ${jahrItem.jahr} ist AKTIV — Stripe-Abrechnung fehlgeschlagen: ${detail}`,
                        'error'
                    );
                } else {
                    alert(`Jahr aktiv, aber Stripe-Fehler: ${detail}`);
                }
            }
        } catch (error: any) {
            console.error("Fehler bei Coach-Freigabe:", error);
            if (showNotification) showNotification(`Fehler: ${error.message}`, 'error');
        } finally {
            coachApproveInFlight.current = false;
            setIsCoachApproving(false);
            setPendingApproveYear(null);
        }
    };

    const handleApprovePasswordConfirm = (password: string) => {
        if (password !== '90') {
            if (showNotification) showNotification('Falsches Passwort.', 'error');
            else alert('Falsches Passwort.');
            setPendingApproveYear(null);
            return;
        }
        const yearItem = pendingApproveYear;
        setApprovePasswordOpen(false);
        void handleCoachApprovalAndBilling(yearItem);
    };

    const handleForceBilling = async () => {
        if (!impersonatedClientId) return;
        
        if (!window.confirm('⚠️ COMMANDER OVERRIDE: Willst du die Stripe-Abrechnung für diesen Mandanten HART auslösen (ohne Statusänderung)? Dieser Vorgang feuert sofort ab!')) return;
        
        setIsBillingLoading(true);
        try {
            // Silent-Mode setzen, damit onMandantUpdated keine zweite Freigabe-Mail sendet
            await updateDoc(doc(db, 'kalkulation_mandanten', impersonatedClientId), {
                lastSilentAdminUpdate: Date.now(),
            });
            const startBilling = httpsCallable(functions, 'startProjectAndBilling');
            await startBilling({ orderId: impersonatedClientId });
            
            if (showNotification) showNotification('Abrechnung wurde erfolgreich hart gestartet!', 'success');
        } catch (error: any) {
            console.error("Fehler beim harten Start der Abrechnung:", error);
            const detail = error?.message || error?.code || 'Unbekannter Fehler';
            if (showNotification) showNotification(`Fehler: ${detail}`, 'error');
        } finally {
            setIsBillingLoading(false);
        }
    };

    const setDevPhase = async (newStatus: string) => {
        const user = auth.currentUser;
        const targetUid = impersonatedClientId || user?.uid;
        if (!targetUid) return;

        try {
            const docRef = doc(db, 'kalkulation_mandanten', targetUid);
            const freshSnap = await getDoc(docRef);
            const freshData = freshSnap.exists() ? freshSnap.data() : {};
            const currentYears = freshData?.clientData?.geschaeftsjahre || freshData?.geschaeftsjahre || [];

            if (!currentYears || currentYears.length === 0) return;

            const targetYearNum = devTargetYear || Math.max(...currentYears.map((j: any) => Number(j.jahr)));
            
            const updatedJahre = currentYears.map((j: any) => {
                if (Number(j.jahr) === targetYearNum) {
                    return { ...j, status: newStatus };
                }
                return j;
            });

            await updateDoc(docRef, {
                "clientData.geschaeftsjahre": updatedJahre,
                geschaeftsjahre: updatedJahre,
                lastSilentAdminUpdate: Date.now() 
            });

            if (typeof setClientData === 'function' && clientData) {
                setClientData({ ...clientData, geschaeftsjahre: updatedJahre });
            }

            // Immer Jahr + Status setzen — sonst bleibt die Sidebar auf altem Status hängen
            localStorage.setItem('CURRENT_YEAR', String(targetYearNum));
            localStorage.setItem('CURRENT_YEAR_STATUS', newStatus);
            try { sessionStorage.setItem('CURRENT_YEAR', String(targetYearNum)); } catch (_) { /* ignore */ }
            try {
                window.dispatchEvent(new CustomEvent('pe-year-status', {
                    detail: { year: String(targetYearNum), status: newStatus },
                }));
            } catch (_) { /* ignore */ }

            if (showNotification) {
                showNotification(`Status für Jahr ${targetYearNum} erfolgreich auf ${newStatus} gesetzt (Silent Mode)`, 'success');
            }
        } catch (error) {
            console.error("Dev-Phase konnte nicht gesetzt werden:", error);
            if (showNotification) showNotification("Fehler beim Wechseln der Phase.", 'error');
        }
    };

    const targetYearForModal = jahre.find((j: any) => j.jahr === yearNeedingUpload);
    const modalHasBwa = targetYearForModal?.bwaUrl || (targetYearForModal?.bwaUrls && targetYearForModal.bwaUrls.length > 0);
    const modalBwaList = targetYearForModal?.bwaUrls || (targetYearForModal?.bwaUrl ? [targetYearForModal.bwaUrl] : []);

    return (
        <div className="min-h-screen bg-deep-space text-slate-200 font-sans flex flex-col items-center relative pb-32 pt-8 selection:bg-phantom-gold selection:text-black">
            <DashboardStyles />
            
            {needsCoachAttention && (
                <div className="w-full max-w-5xl px-4 md:px-8 mb-4 animate-in slide-in-from-top">
                    <div className="bg-yellow-950/40 border-l-4 border-yellow-500 p-4 rounded-r-xl shadow-[0_0_15px_rgba(234,179,8,0.2)] flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <AlertTriangle className="w-6 h-6 text-yellow-500 shrink-0 led-amber" />
                            <div>
                                <h4 className="text-yellow-500 font-bold uppercase tracking-widest text-sm">Achtung Coach: Offene Freigabe!</h4>
                                <p className="text-slate-300 text-xs">Ein oder mehrere Jahre dieses Mandanten warten auf deine Prüfung und Freigabe. Bitte erledige das, bevor du die Akte verlässt.</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showNervPopup && pendingYearToOpen && (
                <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-stone-900/50 dark:bg-black/90 backdrop-blur-sm p-4 animate-fadeIn">
                    <div className="carbon-panel carbon-weave-bg border border-phantom-gold/30 rounded-2xl shadow-[0_0_50px_rgba(230,194,153,0.15)] w-full max-w-lg relative p-8 flex flex-col items-center text-center">
                        <div className="w-16 h-16 bg-[#E6C299]/10 rounded-full flex items-center justify-center mb-6 shadow-inner">
                            <AlertTriangle className="w-8 h-8 led-gold" />
                        </div>
                        <h2 className="text-2xl font-black text-pe-text uppercase tracking-widest mb-4">Wichtiger Hinweis!</h2>
                        <div className="carbon-content text-slate-300 mb-8 leading-relaxed space-y-4">
                            <p>Du betrittst nun deine Jahresakte für die Datenerfassung.</p>
                            <div className="bg-pe-sunken dark:bg-black/60 border border-pe-border-strong p-4 rounded-xl">
                                <strong>Denk daran:</strong> Wenn du alle Daten vollständig eingetragen hast, musst du hier im Dashboard zwingend auf den großen grünen Button <span className="text-emerald-400 font-bold">"Akte vollständig"</span> klicken.
                            </div>
                            <p className="text-sm">Nur so weiß dein Coach, dass du fertig bist, und kann die Abrechnung sowie Prüfung starten!</p>
                        </div>
                        <button 
                            onClick={() => {
                                setShowNervPopup(false);
                                proceedToYear(pendingYearToOpen.jahr, pendingYearToOpen.status);
                                setPendingYearToOpen(null);
                            }}
                            className="bg-[#E6C299] hover:bg-white text-black font-black uppercase tracking-widest text-sm px-8 py-3 rounded-xl transition-colors shadow-lg w-full flex justify-center items-center gap-2 carbon-content"
                        >
                            Verstanden & Akte öffnen <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}

            <main className="w-full max-w-5xl px-4 md:px-8 py-4 space-y-10 animate-fadeIn">
                
                <ClientWelcomeHeader
                    userData={userData}
                    showNotification={showNotification}
                    subtitle="Willkommen in deiner Übersicht. Wähle dein aktives Geschäftsjahr oder beantrage eine neue Kalkulation."
                />

                {!impersonatedClientId && isBillingPending(userData?.billingStatus) && (
                    <div className="carbon-panel carbon-weave-bg p-4 mb-6 rounded-xl border border-amber-500/30 flex items-center gap-3">
                        <span className="w-2.5 h-2.5 rounded-full bg-amber-400 animate-pulse" />
                        <p className="text-sm text-stone-300 font-medium">
                            Deine Akte wird aktuell von deinem Coach finalisiert und für die Abrechnung vorbereitet.
                        </p>
                    </div>
                )}

                {/* Kompakter Melde-Chip: Inhaltsbreite, normale Leseschrift */}
                {jahre.some((j: any) => j.status === 'datenerfassung') && (
                    <div
                        className="group inline-flex w-fit max-w-full flex-wrap items-center gap-3 rounded-xl border border-emerald-500/40 bg-pe-card dark:bg-[#111] px-4 py-3 relative overflow-visible shadow-[0_0_16px_rgba(16,185,129,0.12)]"
                    >
                        <div
                            role="tooltip"
                            className="pointer-events-none absolute left-0 top-[calc(100%+8px)] z-[200] w-[min(20rem,90vw)] rounded-md border border-[#dcb07b] bg-[#E6C299] px-3 py-2 text-sm font-bold text-[#08090b] leading-snug shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-150"
                        >
                            Du bist fertig mit Eintragen? Übertrage die Akte an deinen Coach zur Prüfung und Freischaltung.
                        </div>
                        <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
                        <span className="text-sm font-black text-pe-text uppercase tracking-wider whitespace-nowrap">
                            Fertig mit Eintragen?
                        </span>
                        {!!impersonatedClientId && (
                            <span className="text-xs text-amber-400/90 font-bold uppercase tracking-wider whitespace-nowrap">
                                (God-Mode)
                            </span>
                        )}
                        {jahre
                            .filter((j: any) => j.status === 'datenerfassung')
                            .map((j: any) => (
                                <button
                                    key={`notify-banner-${j.jahr}`}
                                    type="button"
                                    onClick={(e) => handleNotifyCoach(e, j)}
                                    disabled={isSubmittingToCoach}
                                    className="inline-flex w-auto shrink-0 items-center gap-2 rounded-lg px-3 py-2 bg-emerald-600 hover:bg-emerald-500 text-white border border-emerald-400/50 font-black uppercase tracking-wider text-sm disabled:opacity-60"
                                >
                                    {isSubmittingToCoach ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <CheckCircle2 className="w-4 h-4" />
                                    )}
                                    An Coach senden · {j.jahr}
                                </button>
                            ))}
                    </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 items-start">
                    {[...jahre].sort((a: any, b: any) => Number(b.jahr) - Number(a.jahr)).map((jahrItem: any) => {
                        const hasBwa = jahrItem.bwaUrl || (jahrItem.bwaUrls && jahrItem.bwaUrls.length > 0);
                        const uploadedCount = jahrItem.bwaUrls?.length || (jahrItem.bwaUrl ? 1 : 0);
                        const showBranchPicker = hasBranches && branchList.length > 0;
                        const open = isYearExpanded(jahrItem.jahr);
                        const statusBadge =
                            jahrItem.status === 'abgeschlossen' ? (
                                <span className="metal-badge text-pe-muted dark:text-slate-300 px-2.5 py-1 rounded-full text-[10px] font-bold border border-pe-border-strong">Abgeschlossen</span>
                            ) : jahrItem.status === 'aktiv' ? (
                                <span className="bg-emerald-100/80 dark:bg-emerald-950/20 text-emerald-700 dark:text-emerald-400 px-2.5 py-1 rounded-full text-[10px] font-bold border border-emerald-500/50">Kalkulation Aktiv</span>
                            ) : jahrItem.status === 'datenerfassung' ? (
                                <span className="bg-purple-100/80 dark:bg-purple-950/20 text-purple-700 dark:text-purple-400 px-2.5 py-1 rounded-full text-[10px] font-bold border border-purple-500/50 flex items-center gap-1">
                                    <Edit3 className="w-3 h-3" /> Noch in Erfassung
                                </span>
                            ) : jahrItem.status === 'angefragt' ? (
                                <span className="bg-yellow-100/80 dark:bg-yellow-950/20 text-yellow-700 dark:text-yellow-500 px-2.5 py-1 rounded-full text-[10px] font-bold border border-yellow-500/50">
                                    Übertragen · In Prüfung
                                </span>
                            ) : null;

                        const coachPriceBox =
                            !!impersonatedClientId && coachBillingPreview ? (
                                <div className="w-full rounded-xl border border-[#E6C299]/35 bg-[#E6C299]/10 px-3 py-2.5 space-y-1">
                                    <p className="text-[9px] font-black uppercase tracking-[0.18em] text-pe-accent-text dark:text-[#E6C299]">
                                        Abbuchung · Preis
                                    </p>
                                    {coachBillingPreview.isProBono ? (
                                        <p className="text-xs font-bold text-pink-300">Pro Bono — 0,00 €</p>
                                    ) : (
                                        <>
                                            <div className="flex justify-between gap-2 text-[11px] text-pe-muted dark:text-slate-300">
                                                <span>Netto / Monat</span>
                                                <span className="font-mono font-bold text-pe-text dark:text-white">
                                                    {formatEuroDe(coachBillingPreview.netto)} €
                                                </span>
                                            </div>
                                            <div className="flex justify-between gap-2 text-[11px] text-pe-muted dark:text-slate-400">
                                                <span>MwSt. ({coachBillingPreview.mwstSatz} %)</span>
                                                <span className="font-mono">
                                                    {formatEuroDe(coachBillingPreview.brutto - coachBillingPreview.netto)} €
                                                </span>
                                            </div>
                                            <div className="flex justify-between gap-2 text-sm font-black text-pe-accent-text dark:text-[#E6C299] border-t border-[#E6C299]/25 pt-1.5 mt-1">
                                                <span>Brutto / Monat</span>
                                                <span className="font-mono">{formatEuroDe(coachBillingPreview.brutto)} €</span>
                                            </div>
                                        </>
                                    )}
                                </div>
                            ) : null;

                        return (
                        <div
                            key={jahrItem.jahr}
                            className={`carbon-panel shadow-xl relative overflow-hidden border rounded-2xl ${jahrItem.status !== 'angefragt' ? 'carbon-weave-bg border-pe-border dark:border-[#2a2a2a]' : 'bg-pe-card dark:bg-black/40 border-pe-border dark:border-[#2a2a2a] opacity-90'}`}
                        >
                            {/* Mit Filialen: Jahr auf/zuklappen. Ohne Filialen: Klick öffnet Akte direkt. */}
                            {showBranchPicker ? (
                                <button
                                    type="button"
                                    onClick={() => toggleYearExpanded(jahrItem.jahr)}
                                    className="carbon-content w-full flex items-center gap-3 p-4 text-left relative z-10"
                                    aria-expanded={open}
                                >
                                    <div className="metal-badge w-10 h-10 flex items-center justify-center shrink-0">
                                        <Calendar className="w-5 h-5 text-phantom-gold" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="text-2xl font-black font-mono-numbers leading-none text-[#5C3D18] dark:text-[#E6C299]">{jahrItem.jahr}</div>
                                        <div className="mt-1.5 flex flex-wrap items-center gap-2">
                                            {statusBadge}
                                            {!open && (
                                                <span className="text-[10px] font-bold uppercase tracking-wider text-stone-500 dark:text-slate-500">
                                                    {branchList.length} Filiale{branchList.length === 1 ? '' : 'n'} · aufklappen
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <ChevronDown className={`w-5 h-5 text-phantom-gold shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
                                </button>
                            ) : (
                                <div className="carbon-content p-4 relative z-10 space-y-3">
                                    <div className="flex items-center gap-3">
                                        <div className="metal-badge w-10 h-10 flex items-center justify-center shrink-0">
                                            <Calendar className="w-5 h-5 text-phantom-gold" />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="text-2xl font-black font-mono-numbers leading-none text-[#5C3D18] dark:text-[#E6C299]">{jahrItem.jahr}</div>
                                            <div className="mt-1.5 flex flex-wrap gap-2">{statusBadge}</div>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => handleOpenYear(jahrItem)}
                                        className={`inline-flex items-center text-sm font-black transition-colors ${jahrItem.status !== 'angefragt' || impersonatedClientId ? 'text-pe-accent-text dark:text-[#E6C299] hover:text-pe-text dark:hover:text-white' : 'text-stone-500 dark:text-slate-500'}`}
                                    >
                                        {jahrItem.status === 'angefragt' && !impersonatedClientId ? 'Status prüfen' : (!hasBwa && jahrItem.status === 'datenerfassung' ? 'In die Jahresakte eintreten' : 'Akte / Jahr öffnen')}
                                        {(jahrItem.status !== 'angefragt' || impersonatedClientId) && <ArrowRight className="w-4 h-4 ml-2" />}
                                    </button>
                                </div>
                            )}

                            {/* Filialen nur sichtbar, wenn Jahr aufgeklappt */}
                            {showBranchPicker && open && (
                                <div className="carbon-content px-4 pb-4 pt-0 border-t border-pe-border-strong relative z-10 space-y-3">
                                    <div className="space-y-2 pt-3">
                                        <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Filiale wählen</p>
                                        {branchList.map((branch: any) => (
                                            <button
                                                key={`${jahrItem.jahr}-${branch.id}`}
                                                type="button"
                                                onClick={() => {
                                                    if (!impersonatedClientId && jahrItem.status === 'angefragt') {
                                                        if (showNotification) showNotification('Dieses Jahr befindet sich noch in der Prüfung durch deinen Coach.', 'error');
                                                        return;
                                                    }
                                                    void proceedToYear(jahrItem.jahr, jahrItem.status, branch.id);
                                                }}
                                                className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl border border-pe-border-strong bg-black/20 hover:border-phantom-gold/50 text-left transition-colors"
                                            >
                                                <span className="font-bold text-sm text-pe-text dark:text-white truncate">
                                                    {branch.name || branch.id}
                                                    {branch.isMain ? <span className="ml-1.5 text-[9px] uppercase tracking-widest text-emerald-400">Haupt</span> : null}
                                                </span>
                                                <span className="text-xs font-black text-pe-accent-text dark:text-[#E6C299] flex items-center gap-1 shrink-0">
                                                    Öffnen <ArrowRight className="w-3.5 h-3.5" />
                                                </span>
                                            </button>
                                        ))}
                                    </div>

                                    {jahrItem.status === 'datenerfassung' && (
                                        <button
                                            onClick={() => setYearNeedingUpload(jahrItem.jahr)}
                                            className="w-full metal-badge dark:hover:bg-[#111] text-pe-muted dark:text-slate-300 hover:text-pe-text px-3 py-2.5 rounded-lg border border-pe-border-strong transition-colors flex items-center justify-between shadow-sm"
                                        >
                                            <span className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest">
                                                <Upload className="w-3.5 h-3.5 text-phantom-gold" />
                                                {hasBwa ? 'BWA Verwalten' : 'BWA Hochladen'}
                                            </span>
                                            {hasBwa && (
                                                <span className="bg-emerald-950/20 text-emerald-400 px-2 py-0.5 rounded text-[10px] font-black font-mono-numbers border border-emerald-500/50">
                                                    {uploadedCount}
                                                </span>
                                            )}
                                        </button>
                                    )}

                                    {jahrItem.status === 'datenerfassung' && !!impersonatedClientId && (
                                        <button
                                            onClick={(e) => requestCoachApprovalAndBilling(e, jahrItem)}
                                            disabled={isCoachApproving}
                                            className="w-full bg-gradient-to-r from-[#E6C299] to-[#d4b08c] hover:from-[#d4b08c] hover:to-[#c29e7a] text-black border border-[#E6C299] transition-all px-3 py-2.5 rounded-xl flex items-center justify-center gap-2 text-[10px] font-black uppercase tracking-widest shadow-[0_0_20px_rgba(230,194,153,0.3)] hover:scale-[1.02] leading-tight text-center"
                                            title="Kalkulation freischalten"
                                        >
                                            {isCoachApproving ? <Loader2 className="w-4 h-4 animate-spin shrink-0" /> : <Lock className="w-4 h-4 shrink-0" />}
                                            <span className="text-left">
                                                Freischalten<br/>
                                                <span className="text-[8px] text-black/70 font-bold tracking-widest">via Stripe</span>
                                            </span>
                                        </button>
                                    )}

                                    {jahrItem.status === 'angefragt' && (
                                        <div className="space-y-2">
                                            <div className="w-full px-3 py-2.5 rounded-xl border border-yellow-500/40 bg-yellow-950/20 text-yellow-400 text-center text-[10px] font-black uppercase tracking-widest">
                                                Übertragen — wartet auf Freischaltung
                                            </div>
                                            {!!impersonatedClientId && (
                                                <button
                                                    onClick={(e) => requestCoachApprovalAndBilling(e, jahrItem)}
                                                    disabled={isCoachApproving}
                                                    className="w-full bg-gradient-to-r from-[#E6C299] to-[#d4b08c] hover:from-[#d4b08c] hover:to-[#c29e7a] text-black border border-[#E6C299] transition-all px-3 py-2.5 rounded-xl flex items-center justify-center gap-2 text-[10px] font-black uppercase tracking-widest hover:scale-[1.02] leading-tight text-center"
                                                    title="Kalkulation freischalten"
                                                >
                                                    {isCoachApproving ? <Loader2 className="w-4 h-4 animate-spin shrink-0" /> : <Lock className="w-4 h-4 shrink-0" />}
                                                    <span className="text-left">
                                                        Freischalten<br/>
                                                        <span className="text-[8px] text-black/70 font-bold tracking-widest">via Stripe</span>
                                                    </span>
                                                </button>
                                            )}
                                        </div>
                                    )}
                                    {coachPriceBox}
                                </div>
                            )}

                            {/* Ohne Filialen: BWA / Coach-Aktionen direkt auf der Karte */}
                            {!showBranchPicker && (
                                <div className="carbon-content px-4 pb-4 pt-0 relative z-10 space-y-2">
                                    {jahrItem.status === 'datenerfassung' && (
                                        <button
                                            onClick={() => setYearNeedingUpload(jahrItem.jahr)}
                                            className="w-full metal-badge dark:hover:bg-[#111] text-pe-muted dark:text-slate-300 hover:text-pe-text px-3 py-2.5 rounded-lg border border-pe-border-strong transition-colors flex items-center justify-between shadow-sm"
                                        >
                                            <span className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest">
                                                <Upload className="w-3.5 h-3.5 text-phantom-gold" />
                                                {hasBwa ? 'BWA Verwalten' : 'BWA Hochladen'}
                                            </span>
                                            {hasBwa && (
                                                <span className="bg-emerald-950/20 text-emerald-400 px-2 py-0.5 rounded text-[10px] font-black font-mono-numbers border border-emerald-500/50">
                                                    {uploadedCount}
                                                </span>
                                            )}
                                        </button>
                                    )}

                                    {jahrItem.status === 'datenerfassung' && !!impersonatedClientId && (
                                        <button
                                            onClick={(e) => requestCoachApprovalAndBilling(e, jahrItem)}
                                            disabled={isCoachApproving}
                                            className="w-full bg-gradient-to-r from-[#E6C299] to-[#d4b08c] hover:from-[#d4b08c] hover:to-[#c29e7a] text-black border border-[#E6C299] transition-all px-3 py-2.5 rounded-xl flex items-center justify-center gap-2 text-[10px] font-black uppercase tracking-widest shadow-[0_0_20px_rgba(230,194,153,0.3)] hover:scale-[1.02] leading-tight text-center"
                                            title="Kalkulation freischalten"
                                        >
                                            {isCoachApproving ? <Loader2 className="w-4 h-4 animate-spin shrink-0" /> : <Lock className="w-4 h-4 shrink-0" />}
                                            <span className="text-left">
                                                Freischalten<br/>
                                                <span className="text-[8px] text-black/70 font-bold tracking-widest">via Stripe</span>
                                            </span>
                                        </button>
                                    )}

                                    {jahrItem.status === 'angefragt' && (
                                        <div className="space-y-2">
                                            <div className="w-full px-3 py-2.5 rounded-xl border border-yellow-500/40 bg-yellow-950/20 text-yellow-400 text-center text-[10px] font-black uppercase tracking-widest">
                                                Übertragen — wartet auf Freischaltung
                                            </div>
                                            {!!impersonatedClientId && (
                                                <button
                                                    onClick={(e) => requestCoachApprovalAndBilling(e, jahrItem)}
                                                    disabled={isCoachApproving}
                                                    className="w-full bg-gradient-to-r from-[#E6C299] to-[#d4b08c] hover:from-[#d4b08c] hover:to-[#c29e7a] text-black border border-[#E6C299] transition-all px-3 py-2.5 rounded-xl flex items-center justify-center gap-2 text-[10px] font-black uppercase tracking-widest hover:scale-[1.02] leading-tight text-center"
                                                    title="Kalkulation freischalten"
                                                >
                                                    {isCoachApproving ? <Loader2 className="w-4 h-4 animate-spin shrink-0" /> : <Lock className="w-4 h-4 shrink-0" />}
                                                    <span className="text-left">
                                                        Freischalten<br/>
                                                        <span className="text-[8px] text-black/70 font-bold tracking-widest">via Stripe</span>
                                                    </span>
                                                </button>
                                            )}
                                        </div>
                                    )}
                                    {coachPriceBox}
                                </div>
                            )}
                        </div>
                        );
                    })}
                </div>

                <div className="w-full h-px bg-[#333] my-8" />

                {/* --- OPERATION "BWA-ON-DEMAND" --- */}
                <div className="carbon-panel carbon-weave-bg border border-[#2a2a2a] rounded-3xl p-6 md:p-8 shadow-xl relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-1 h-full bg-[#E6C299]/50"></div>
                    <div className="carbon-content flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                        <div>
                            <h2 className="text-xl font-bold text-pe-text mb-2 flex items-center gap-3">
                                <FileCheck className="w-6 h-6 led-gold" />
                                Aktuelle BWA einreichen
                            </h2>
                            <p className="text-slate-400 text-sm max-w-2xl">
                                Du hast weitere oder korrigierte BWA-Dokumente von deinem Steuerberater erhalten? Über diesen Upload-Hub kannst du jederzeit manuell PDFs für dein aktuellstes Geschäftsjahr nachreichen.
                            </p>
                        </div>
                        <button 
                            onClick={() => {
                                const latestYear = Math.max(...jahre.map((j: any) => Number(j.jahr)));
                                setYearNeedingUpload(latestYear);
                            }}
                            className="metal-badge dark:hover:bg-[#111] text-pe-text px-6 py-4 rounded-xl border border-pe-border-strong transition-all flex items-center gap-2 shadow-[0_0_20px_rgba(230,194,153,0.1)] hover:shadow-[0_0_30px_rgba(230,194,153,0.3)] whitespace-nowrap font-bold tracking-wider uppercase text-sm"
                        >
                            <Upload className="w-4 h-4 text-phantom-gold" />
                            BWA UPLOAD ÖFFNEN
                        </button>
                    </div>
                </div>

                <div className="w-full h-px bg-[#333] my-8" />

                <div className="carbon-panel carbon-weave-bg border border-red-900/30 p-6 md:p-8 shadow-xl relative overflow-visible pe-help-tip">
                    <span className="pe-help-tip-bubble" role="tooltip">
                        <strong className="text-[#E6C299]">Was passiert beim System-Rollover?</strong><br />
                        1) Letztes offenes Jahr wird auf Status „abgeschlossen“ gesetzt.<br />
                        2) Die Akte wird schreibgeschützt (Panzerglas), damit keine Nachträge mehr reinrutschen.<br />
                        3) Es wird noch <em>kein</em> neues Jahr angelegt — das ist der zweite Schritt darunter.<br /><br />
                        Nur ausführen, wenn alle Umsätze/BWA des abgelaufenen Jahres fertig sind. Nicht rückgängig machbar.
                    </span>
                    <div className="absolute top-0 left-0 w-1 h-full bg-red-500/50 rounded-l-inherit"></div>
                    <div className="carbon-content flex flex-col gap-6">
                        <div>
                            <h2 className="text-xl font-bold text-red-400 mb-2 flex items-center gap-3">
                                <AlertTriangle className="w-6 h-6 led-red" />
                                System-Rollover: Jahreswechsel durchführen
                            </h2>
                            <p className="text-slate-400 text-sm max-w-2xl">
                                Schließt das <strong className="text-slate-200">aktuelle Geschäftsjahr</strong> ab und sperrt die Akte.
                                Durchschnittswerte bleiben als Vorjahres-Vergleich erhalten.
                                <strong className="text-red-300 ml-1">Es wird dabei noch kein neues Jahr eröffnet.</strong>
                                {' '}Das Folgejahr legst du erst im Kasten darunter an.
                            </p>
                        </div>

                        <div className="bg-pe-sunken dark:bg-black/60 p-4 rounded-xl border border-pe-border-strong">
                            <label className={`flex items-start gap-3 group ${rolloverConfirm ? 'cursor-not-allowed opacity-80' : 'cursor-pointer'}`}>
                                <div className="pt-0.5">
                                    <input 
                                        type="checkbox" 
                                        checked={rolloverConfirm}
                                        onChange={(e) => setRolloverConfirm(e.target.checked)}
                                        disabled={rolloverConfirm}
                                        className={`form-checkbox h-5 w-5 rounded focus:ring-red-500/50 transition-colors ${rolloverConfirm ? 'text-red-500/50 bg-pe-tile-header dark:bg-[#111] border-pe-border-strong cursor-not-allowed' : 'text-red-500 bg-pe-input dark:bg-black border-pe-border-strong dark:border-[#444] cursor-pointer'}`}
                                    />
                                </div>
                                <span className={`text-sm font-bold transition-colors ${rolloverConfirm ? 'text-pe-muted dark:text-slate-400' : 'text-pe-muted dark:text-slate-300 group-hover:text-pe-text'}`}>
                                    Ich bestätige hiermit, dass alle Umsätze für das abgelaufene Jahr vollständig und korrekt eingetragen sind und der Daten-Rollover endgültig durchgeführt werden darf.
                                </span>
                            </label>
                        </div>

                        <div>
                            <button 
                                title={rolloverConfirm
                                    ? 'Schließt das aktuelle Jahr ab. Legt noch KEIN neues Jahr an.'
                                    : 'Zuerst Bestätigung setzen (Checkbox).'}
                                onClick={async () => {
                                    if (rolloverConfirm && executeJahreswechsel) {
                                        executeJahreswechsel();

                                        try {
                                            const user = auth.currentUser;
                                            const targetUid = impersonatedClientId || user?.uid;
                                            
                                            if (targetUid) {
                                                const docRef = doc(db, 'kalkulation_mandanten', targetUid);
                                                const freshSnap = await getDoc(docRef);
                                                const freshData = freshSnap.exists() ? freshSnap.data() : {};
                                                const currentYears = freshData?.clientData?.geschaeftsjahre || freshData?.geschaeftsjahre || [];

                                                if (currentYears.length > 0) {
                                                    const latestYearNum = Math.max(...currentYears.map((j: any) => Number(j.jahr)));
                                                    const updatedJahre = currentYears.map((j: any) => {
                                                        if (Number(j.jahr) === latestYearNum) {
                                                            return { ...j, status: 'abgeschlossen' };
                                                        }
                                                        return j;
                                                    });
                                                    
                                                    await updateDoc(docRef, {
                                                        "clientData.geschaeftsjahre": updatedJahre,
                                                        geschaeftsjahre: updatedJahre
                                                    });

                                                    if (typeof setClientData === 'function') {
                                                        const freshClientData = freshData.clientData || {};
                                                        setClientData({ ...freshClientData, geschaeftsjahre: updatedJahre });
                                                    }

                                                    localStorage.setItem('CURRENT_YEAR_STATUS', 'abgeschlossen');
                                                }
                                            }
                                        } catch (e) {
                                            console.error("Fehler beim Status-Update:", e);
                                        }

                                        if (showNotification) {
                                            showNotification('Jahreswechsel erfolgreich! Das Jahr wurde abgeschlossen.', 'success');
                                        } else {
                                            alert('Jahreswechsel erfolgreich! Das Jahr wurde abgeschlossen.');
                                        }
                                    }
                                }}
                                disabled={!rolloverConfirm}
                                className={`
                                    metal-badge flex items-center gap-2 px-6 py-4 rounded-xl font-bold tracking-wider uppercase text-sm transition-all
                                    ${rolloverConfirm 
                                        ? 'bg-red-900/80 hover:bg-red-800 text-white border-red-700 shadow-[0_0_20px_rgba(220,38,38,0.2)]' 
                                        : 'bg-pe-sunken dark:bg-black text-stone-400 dark:text-slate-600 cursor-not-allowed border-pe-border-strong'}
                                `}
                            >
                                {!rolloverConfirm ? <Lock className="w-4 h-4" /> : <Settings className="w-4 h-4" />}
                                Daten-Rollover jetzt ausführen
                            </button>
                        </div>
                    </div>
                </div>

                <div className="w-full h-px bg-[#333] my-8" />

                <div className="carbon-panel carbon-weave-bg border border-[#2a2a2a] rounded-3xl p-6 md:p-8 shadow-xl relative overflow-visible pe-help-tip">
                    <span className="pe-help-tip-bubble" role="tooltip">
                        <strong className="text-[#E6C299]">Was passiert beim neuen Geschäftsjahr?</strong><br />
                        Legt das Folgejahr (z.&nbsp;B. 2026 → 2027) neu an und öffnet die Datenerfassung.<br />
                        Umsätze/BWA des neuen Jahres starten leer; Vorjahreswerte bleiben zum Vergleich.<br /><br />
                        Nur nach System-Rollover möglich. Es erscheint immer eine Sicherheitsabfrage — Speichern in der Akte legt kein Jahr an.
                    </span>
                    <div className="carbon-content flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                        <div>
                            <h2 className="text-xl font-bold text-pe-text mb-2 flex items-center gap-3">
                                <PlusCircle className="w-6 h-6 text-phantom-gold" />
                                Neues Geschäftsjahr anlegen
                            </h2>
                            <p className="text-slate-400 text-sm max-w-xl">
                                Zweiter Schritt nach dem Rollover: erstellt bewusst das <strong className="text-slate-200">Folgejahr</strong> als neue Jahres-Akte.
                                {rolloverConfirm 
                                    ? " Mit Bestätigungsdialog — kein stilles Auto-Anlegen." 
                                    : " Erst freigeschaltet, wenn der System-Rollover (oben) durch ist."}
                            </p>
                        </div>

                        <button 
                            onClick={handleCreateNewYear}
                            disabled={!rolloverConfirm}
                            title={rolloverConfirm
                                ? 'Folgejahr anlegen (mit Bestätigung). Speichern allein tut das nicht.'
                                : 'Zuerst System-Rollover durchführen.'}
                            className={`
                                metal-badge flex items-center gap-2 px-6 py-4 rounded-xl font-bold tracking-wider uppercase text-sm transition-all whitespace-nowrap
                                ${rolloverConfirm 
                                    ? 'dark:hover:bg-[#111] text-pe-text border-pe-border-strong shadow-[0_0_20px_rgba(230,194,153,0.1)]' 
                                    : 'bg-pe-sunken dark:bg-black text-stone-400 dark:text-slate-600 cursor-not-allowed border-pe-border-strong'}
                            `}
                        >
                            {!rolloverConfirm ? <Lock className="w-4 h-4" /> : <PlusCircle className="w-4 h-4 text-phantom-gold" />}
                            Neues Jahr starten
                        </button>
                    </div>
                </div>

            </main>

            {yearNeedingUpload !== null && (
                <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-stone-900/50 dark:bg-black/90 backdrop-blur-sm p-4 md:p-8 animate-fadeIn">
                    <div className="carbon-panel carbon-weave-bg border border-pe-border-strong rounded-2xl shadow-2xl w-full max-w-2xl relative overflow-hidden p-8 flex flex-col gap-6">
                        <div className={`absolute top-0 left-0 w-1 h-full ${modalHasBwa ? 'bg-blue-500' : 'bg-red-500'}`}></div>
                        
                        <div className="carbon-content flex justify-between items-start">
                            <div>
                                <h3 className="text-pe-text font-bold text-2xl mb-2 flex items-center gap-3">
                                    {modalHasBwa ? (
                                        <><FileCheck className="w-7 h-7 text-blue-400" /> BWA-Verwaltungs-Hub (<span className="font-mono-numbers">{yearNeedingUpload}</span>)</>
                                    ) : (
                                        <><AlertTriangle className="w-7 h-7 text-red-500" /> BWA Fehlt! (<span className="font-mono-numbers">{yearNeedingUpload}</span>)</>
                                    )}
                                </h3>
                                <p className="text-slate-400">
                                    {modalHasBwa 
                                        ? `Hier siehst du alle hochgeladenen Dokumente. Du kannst falsche PDFs löschen oder über das Feld unten weitere anhängen.`
                                        : `Lade hier deine aktuelle BWA hoch. Für den Jahresabschluss benötigen wir zwingend die finale Dezember-BWA des Vorjahres. Du kannst hier auch mehrere PDFs auf einmal markieren.`}
                                </p>
                            </div>
                            <button onClick={() => setYearNeedingUpload(null)} className="text-stone-500 dark:text-slate-500 hover:text-pe-text p-2 metal-badge transition-colors">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {modalHasBwa && modalBwaList.length > 0 && (
                            <div className="carbon-content bg-pe-sunken dark:bg-black border border-pe-border-strong rounded-xl p-4 flex flex-col gap-2">
                                <h4 className="text-[10px] text-slate-500 uppercase tracking-widest font-bold mb-2">Hochgeladene Dateien:</h4>
                                {modalBwaList.map((url: string, idx: number) => (
                                    <div key={idx} className="flex items-center justify-between bg-pe-card dark:bg-[#111] border border-pe-border dark:border-[#222] p-3 rounded-lg">
                                        <div className="flex items-center gap-3">
                                            <FileText className="w-5 h-5 text-blue-400" />
                                            <span className="text-sm font-bold font-mono-numbers text-slate-300">BWA_Dokument_Teil_{idx + 1}.pdf</span>
                                        </div>
                                        <button 
                                            onClick={() => handleDeleteSingleBwa(url, yearNeedingUpload)}
                                            disabled={isDeletingUrl === url || isUploading}
                                            className="text-red-500 hover:text-red-400 p-2 bg-red-950/20 hover:bg-red-950/40 border border-red-500/20 rounded transition-colors"
                                            title="Datei unwiderruflich löschen"
                                        >
                                            {isDeletingUrl === url ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        {!modalHasBwa && (
                            <div className="carbon-content bg-red-950/20 border border-red-900/50 rounded-xl p-5 shadow-inner">
                                <ul className="text-slate-300 text-sm space-y-2 list-disc list-inside pl-2 marker:text-red-500/70">
                                    <li>Lade für den Jahresabschluss die <strong>Dezember-BWA</strong> des Vorjahres hoch, oder bei Bedarf deine <strong>aktuelle unterjährige BWA</strong>.</li>
                                    <li>Die BWA muss zwingend den <strong>Wertenachweis</strong> (Kontennachweis) enthalten <strong>(keine Summen- und Saldenliste!)</strong>. Schau dir dazu bitte die Muster-BWA an.</li>
                                    <li><strong>Keine Fotos!</strong> Wir benötigen das originale PDF deines Steuerberaters.</li>
                                </ul>
                            </div>
                        )}

                        <div className="carbon-content w-full h-32 relative group">
                            <input 
                                id="bwa-upload-input"
                                type="file" 
                                accept=".pdf" 
                                multiple
                                onChange={(e) => handleLateBwaUpload(e, yearNeedingUpload)}
                                disabled={isUploading}
                                className="hidden" 
                            />
                            <label 
                                htmlFor="bwa-upload-input"
                                className={`w-full h-full border-2 border-dashed rounded-2xl transition-all flex flex-col items-center justify-center cursor-pointer 
                                    ${modalHasBwa ? 'border-blue-900/50 bg-blue-950/10 hover:border-blue-500' : 'border-red-900/50 bg-red-950/20 hover:border-red-500'} 
                                    ${isUploading ? 'opacity-50 pointer-events-none' : ''}`}
                            >
                                {isUploading ? (
                                    <Loader2 className={`w-8 h-8 animate-spin mb-2 ${modalHasBwa ? 'text-blue-400' : 'text-red-400'}`} />
                                ) : (
                                    <Upload className={`w-8 h-8 mb-2 transition-colors group-hover:-translate-y-1 ${modalHasBwa ? 'text-blue-900/50 group-hover:text-blue-400' : 'text-red-900/50 group-hover:text-red-400'}`} />
                                )}
                                <span className={`text-sm font-bold transition-colors ${modalHasBwa ? 'text-blue-400/70 group-hover:text-blue-400' : 'text-red-400/70 group-hover:text-red-400'}`}>
                                    {isUploading ? 'Dokumente werden hochgeladen...' : 'Neues PDF hier auswählen oder reinziehen'}
                                </span>
                            </label>
                        </div>

                        <div className="carbon-content flex justify-between items-center pt-2 border-t border-pe-border-strong">
                            <button 
                                type="button"
                                onClick={() => {
                                    const targetJahr = jahre.find((j: any) => j.jahr === yearNeedingUpload);
                                    if (targetJahr) {
                                        setYearNeedingUpload(null);
                                        openYearWithNervCheck(targetJahr);
                                    }
                                }} 
                                className="text-stone-500 dark:text-slate-500 hover:text-pe-text text-xs font-bold uppercase tracking-widest transition-colors underline"
                            >
                                {modalHasBwa ? 'Schließen & Akte öffnen' : 'Später hochladen & Akte öffnen'}
                            </button>

                            <button 
                                type="button"
                                onClick={() => setShowBwaModal(true)} 
                                className="inline-flex items-center gap-2 text-phantom-gold hover:text-pe-text text-xs font-bold uppercase tracking-widest transition-colors"
                            >
                                <Download className="w-4 h-4" /> Muster-BWA ansehen
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showBwaModal && (
                <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-stone-900/50 dark:bg-black/90 backdrop-blur-sm p-4 md:p-8 animate-fadeIn">
                    <div className="carbon-panel carbon-weave-bg border border-pe-border-strong rounded-2xl shadow-2xl w-full max-w-5xl h-[85vh] flex flex-col relative overflow-hidden">
                        <div className="carbon-content p-4 border-b border-pe-border-strong flex justify-between items-center bg-pe-tile-header">
                            <h3 className="text-pe-text font-bold flex items-center gap-2">
                                <FileText className="w-5 h-5 text-phantom-gold" /> Muster-BWA (Beispiel)
                            </h3>
                            <button onClick={() => setShowBwaModal(false)} className="text-stone-500 dark:text-slate-500 hover:text-pe-text p-2 metal-badge transition-colors">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="carbon-content flex-1 bg-pe-sunken dark:bg-black p-2 relative">
                            <iframe 
                                src="/assets/muster-bwa.pdf" 
                                className="w-full h-full rounded-xl bg-white"
                                title="Muster BWA"
                            />
                        </div>
                        <div className="carbon-content p-4 border-t border-pe-border-strong bg-pe-tile-header flex justify-between items-center">
                            <a 
                                href="/assets/muster-bwa.pdf" 
                                download="Muster_BWA_PreisExpert.pdf"
                                className="text-slate-400 hover:text-phantom-gold text-xs font-bold uppercase tracking-widest flex items-center gap-2 transition-colors"
                            >
                                <Download className="w-4 h-4" /> PDF Herunterladen
                            </a>
                            <button onClick={() => setShowBwaModal(false)} className="metal-badge dark:hover:bg-[#111] text-pe-text px-6 py-2 rounded-lg text-sm font-bold transition-colors">
                                Schließen
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {!!impersonatedClientId && (
                <div className="fixed bottom-0 left-0 right-0 carbon-console !border-b-0 !border-l-0 !border-r-0 !rounded-none !mb-0 !padding-0 z-50 shadow-[0_-20px_50px_rgba(0,0,0,0.9)] max-w-[100vw] overflow-x-hidden pb-[calc(env(safe-area-inset-bottom,0px)+3.5rem)] md:pb-0 md:pr-24">
                    <div className="carbon-content flex flex-col gap-3 p-3 md:p-4 max-w-[1600px] mx-auto w-full min-w-0">
                        <div className="flex flex-col gap-2 min-w-0 w-full">
                            <div className="flex items-center gap-2 text-slate-500 font-bold text-xs uppercase tracking-widest shrink-0">
                                <Settings className="w-4 h-4 animate-spin-slow" /> Dev Mode:
                            </div>

                            <div className="flex items-center gap-2 min-w-0 w-full overflow-x-auto overscroll-x-contain pb-1 [-webkit-overflow-scrolling:touch]">
                                <select
                                    className="shrink-0 bg-pe-input dark:bg-black border border-pe-border-strong text-pe-text rounded px-3 py-2 text-xs font-bold font-mono-numbers outline-none focus:border-phantom-gold transition-colors cursor-pointer"
                                    value={devTargetYear || ""}
                                    onChange={(e) => setDevTargetYear(Number(e.target.value))}
                                    title="Wähle das Jahr aus"
                                >
                                    {jahre.map((j: any) => (
                                        <option key={j.jahr} value={j.jahr}>Jahr {j.jahr}</option>
                                    ))}
                                </select>

                                <button
                                    onClick={handleRescueGhostAkte}
                                    disabled={isRescuing}
                                    className="shrink-0 px-3 py-2 bg-blue-950/20 text-blue-400 border border-blue-500/30 rounded hover:bg-blue-950/40 text-xs font-bold transition-all whitespace-nowrap shadow-[0_0_8px_rgba(59,130,246,0.2)] flex items-center gap-2"
                                    title="Repariert kaputte/kopierte Akten durch Anlage der Array-Struktur ohne Datenverlust"
                                >
                                    {isRescuing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Settings className="w-3 h-3" />}
                                    🛠️ GEISTER-AKTE FIX
                                </button>
                                <button onClick={() => setDevPhase('angefragt')} className="shrink-0 px-3 py-2 bg-yellow-950/20 text-yellow-500 border border-yellow-500/30 rounded hover:bg-yellow-950/40 text-xs font-bold transition-all whitespace-nowrap shadow-[0_0_8px_rgba(234,179,8,0.2)]" title="Ändert den Status nur für die Präsentation (Keine E-Mail)">P1: Angefragt</button>
                                <button onClick={() => setDevPhase('datenerfassung')} className="shrink-0 px-3 py-2 bg-purple-950/20 text-purple-400 border border-purple-500/30 rounded hover:bg-purple-950/40 text-xs font-bold transition-all whitespace-nowrap shadow-[0_0_8px_rgba(168,85,247,0.2)]" title="Ändert den Status nur für die Präsentation (Keine E-Mail)">P2: Daten</button>
                                <button onClick={() => setDevPhase('aktiv')} className="shrink-0 px-3 py-2 bg-emerald-950/20 text-emerald-400 border border-emerald-500/30 rounded hover:bg-emerald-950/40 text-xs font-bold transition-all whitespace-nowrap shadow-[0_0_8px_rgba(16,185,129,0.2)]" title="Ändert den Status nur für die Präsentation (Keine E-Mail)">P3: Aktiv</button>
                                <button onClick={() => setDevPhase('abgeschlossen')} className="shrink-0 px-3 py-2 bg-pe-card dark:bg-black text-pe-muted dark:text-slate-300 border border-pe-border-strong dark:border-[#444] rounded hover:bg-pe-hover dark:hover:bg-[#111] text-xs font-bold transition-all whitespace-nowrap" title="Ändert den Status nur für die Präsentation (Keine E-Mail)">P4: Archiv</button>
                            </div>
                        </div>

                        {isAdminUser && (
                            <div className="flex flex-col sm:flex-row items-stretch gap-2 w-full min-w-0 border-t border-pe-border-strong pt-3">
                                <button
                                    onClick={handleForceBilling}
                                    disabled={isBillingLoading}
                                    className="flex-1 px-4 py-3 bg-red-950/40 hover:bg-red-900/60 text-red-400 border border-red-500/50 rounded text-xs font-black tracking-widest uppercase transition-all shadow-[0_0_20px_rgba(220,38,38,0.3)] flex items-center justify-center gap-2 whitespace-nowrap"
                                    title="Zwingt Stripe zum Einzug OHNE den Aktenstatus zu verändern"
                                >
                                    {isBillingLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertTriangle className="w-4 h-4" />}
                                    Nur Abrechnung hart starten
                                </button>
                            </div>
                        )}
                        <div className="flex flex-col sm:flex-row items-stretch gap-2 w-full min-w-0 border-t border-pe-border-strong pt-3">
                            <button
                                onClick={() => { window.location.href = '/onboarding'; }}
                                className="flex-1 px-4 py-3 bg-blue-950/40 hover:bg-blue-900/60 text-blue-400 border border-blue-500/50 rounded text-xs font-black tracking-widest uppercase transition-all shadow-[0_0_20px_rgba(37,99,235,0.3)] flex items-center justify-center gap-2 whitespace-nowrap"
                                title="Öffnet das Onboarding-Modul für diesen Mandanten"
                            >
                                <FileText className="w-4 h-4" />
                                ONBOARDING ANSICHT
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <PasswordPrompt
                isOpen={approvePasswordOpen}
                onClose={() => {
                    setApprovePasswordOpen(false);
                    setPendingApproveYear(null);
                }}
                onConfirm={handleApprovePasswordConfirm}
                title="Kalkulation freischalten"
            />

        </div>
    );
};

export default ClientDashboard;